/**
 * Agent Robot 气泡展示相关工具函数
 */

// 当日气泡展示记录（localStorage，存储展示日期字符串）
const BUBBLE_SHOWN_DATE_KEY = '__dba_agent_bubble_shown_date__';

// 用户离开时间记录（localStorage，存储毫秒时间戳）
const LEAVE_TIME_KEY = '__dba_agent_leave_time__';

// 1 小时毫秒数
const ONE_HOUR_MS = 60 * 60 * 1000;

// 用户离开超 1h 回来时的随机问候语（中英文各 10 条）
const RETURN_PROMPTS: Record<string, string[]> = {
  'zh-CN': [
    '主人～您的专属游戏数据 AI 已上线，DataBrain 数据问我便知，随时待命绝不掉线～',
    '主人～我超会在 DataBrain 扒数据、分析、洞察、随叫随到绝不摸鱼～',
    '主人～我来咯！DataBrain游戏数据这块我直接拿捏，有活尽管甩过来！',
    'Hi 主人～您的专属游戏数据AI已到工位！数据分析都包在我身上～',
    '主人～您回来啦！DataBrain今日游戏数据已更新，戳我问问游戏表现！',
    '主人～您的专属游戏数据AI助手已就位！整理数据、生成海报都超在行的～',
    '主人～您的专属游戏数据AI来啦！DataBrain全球游戏数据随时问，主打一个秒回～',
    '主人～Brainy在呢！数据任务尽管交给我，从查询分析到洞察报告，我来搞定！',
    '主人～Brainy上线啦！DataBrain全球游戏数据洞察，全天在线，随时解答～',
    'Hi 主人，我是Brainy～您的专属游戏数据AI，查数、分析、洞察，有需求尽管吩咐！',
  ],
  'en-US': [
    'Hi there~ Your personal game data AI is now online! Just ask and I\'ve got the answers from DataBrain—always on, always ready!',
    'Hey there~ I\'m good at finding data, doing analysis, and discovering insights from DataBrain. I\'m always here whenever you need.',
    'Hey, I\'m here! When it comes to DataBrain game data, I\'ve got it covered. Throw any tasks to me.',
    'Hi there~ Your personal game data AI is at your service! Leave all data analysis to me.',
    'Welcome back~ Today\'s DataBrain game data has updated. Tap me to check how your game is performing!',
    'Brainy is all set! Your personal game data AI is ready for tasks. From organizing data to generating reports, I\'ve got you covered.',
    'Hey, I\'m here! Your personal game data AI. I\'ve got global game data insights from DataBrain. Get instant answers right away.',
    'Your Brainy is here! From data queries to deep insights and reports—just leave it to me.',
    'Brainy is online! Your go-to partner for global game data insights—always available, always responsive.',
    'Hi there, I\'m Brainy~ your personal game data AI. Game data, analysis, and smart insights—just tell me what you need!',
  ],
};

/**
 * 判断今天是否已经展示过气泡
 */
export const hasBeenShownToday = (): boolean => {
  try {
    const stored = localStorage.getItem(BUBBLE_SHOWN_DATE_KEY);
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD (UTC)
    return stored === today;
  } catch (error) {
    console.warn('[bubbleHelpers] Failed to access localStorage:', error);
    return false;
  }
};

/**
 * 标记今天已展示过气泡
 */
export const markAsShownToday = (): void => {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD (UTC)
    localStorage.setItem(BUBBLE_SHOWN_DATE_KEY, today);
  } catch (error) {
    console.warn('[bubbleHelpers] Failed to write to localStorage:', error);
  }
};

/**
 * 记录用户离开当前页面的时刻
 */
export const recordLeaveTime = (): void => {
  try {
    localStorage.setItem(LEAVE_TIME_KEY, String(Date.now()));
  } catch (error) {
    console.warn('[bubbleHelpers] Failed to record leave time:', error);
  }
};

/**
 * 判断用户距离上次离开是否超过 1 小时
 */
export const hasLongAbsence = (): boolean => {
  try {
    const leaveTime = localStorage.getItem(LEAVE_TIME_KEY);
    if (!leaveTime) return false;
    return Date.now() - Number(leaveTime) > ONE_HOUR_MS;
  } catch (error) {
    console.warn('[bubbleHelpers] Failed to check leave time:', error);
    return false;
  }
};

/**
 * 清除离开时间记录（气泡触发后调用，避免重复弹出）
 */
export const clearLeaveTime = (): void => {
  try {
    localStorage.removeItem(LEAVE_TIME_KEY);
  } catch (error) {
    console.warn('[bubbleHelpers] Failed to clear leave time:', error);
  }
};

/**
 * 从 10 条问候语中随机抽取一条
 * @param locale 当前语言，'zh-CN' 或 'en-US'
 */
export const getRandomReturnPrompt = (locale: string): string => {
  const prompts = RETURN_PROMPTS[locale] ?? RETURN_PROMPTS['zh-CN'];
  return prompts[Math.floor(Math.random() * prompts.length)];
};

/**
 * Promise 延迟工具函数
 * @param ms 延迟毫秒数（0-2147483647）
 * @returns Promise<void>
 */
export const delay = (ms: number): Promise<void> => {
  // 参数校验：处理负数和非有限数
  if (ms < 0 || !Number.isFinite(ms)) {
    console.warn(`[bubbleHelpers] Invalid delay value: ${ms}, using 0 instead`);
    ms = 0;
  }
  
  // 参数校验：处理超过 setTimeout 最大值的情况
  if (ms > 2147483647) {
    console.warn(`[bubbleHelpers] Delay value too large: ${ms}, using max value 2147483647`);
    ms = 2147483647;
  }
  
  return new Promise(resolve => setTimeout(resolve, ms));
};
